import { insertAtCursor } from './insert-text'

export type TextField = HTMLInputElement | HTMLTextAreaElement

export interface SavedSelection {
  start: number
  end: number
}

/** Читает выделение поля; для нетекстовых Input (`selectionStart === null`) — null. */
export function readSelection(el: TextField | null): SavedSelection | null {
  if (!el) return null
  const start = el.selectionStart
  const end = el.selectionEnd
  if (start === null || end === null) return null
  return { start, end }
}

/**
 * Выставляет значение контролируемого поля «как будто ввёл пользователь»: нативный
 * value-setter (минуя трекер React) + событие `input` → срабатывает React `onChange`.
 * Fallback для мест без явного `onVoiceInsert`; не работает там, где значение
 * сохраняется мимо `onChange` (onBlur-сохранение, debounce, contentEditable).
 */
export function setNativeValue(el: TextField, value: string): void {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
  const descriptor = Object.getOwnPropertyDescriptor(proto, 'value')
  if (descriptor?.set) {
    descriptor.set.call(el, value)
  } else {
    el.value = value
  }
  el.dispatchEvent(new Event('input', { bubbles: true }))
}

/**
 * Вставляет `text` в поле на позицию курсора (или сохранённую, если поле потеряло фокус;
 * или в конец, если поле ни разу не было в фокусе). Возвращает новое значение и каретку.
 * Само значение НЕ применяет — это делает вызывающий (явный колбэк или setNativeValue).
 */
export function computeFieldInsert(el: TextField | null, currentValue: string, text: string, saved: SavedSelection | null) {
  const live = el && typeof document !== 'undefined' && document.activeElement === el ? readSelection(el) : null
  const sel = live ?? saved
  return insertAtCursor(currentValue, text, sel?.start ?? null, sel?.end ?? null)
}

/** Возвращает фокус в поле и ставит каретку после вставки (после применения нового значения). */
export function restoreCaret(el: TextField | null, caret: number): void {
  if (!el) return
  requestAnimationFrame(() => {
    try {
      el.focus({ preventScroll: true })
      el.setSelectionRange(caret, caret)
    } catch {
      // нетекстовые input не поддерживают setSelectionRange
    }
  })
}
